import { type DriveFile } from '@/shared/DriveTypes';
import { Skeleton } from '@mui/material';
import cx from 'classnames';
import { useSWRConfig } from 'swr';
import { Link, useLocation } from 'wouter';
import { flex, justifyEnd } from '~/app.css';
import * as pageStyles from '~/components/Page/Page.css';
import { LEGACY, search } from '~/utils/constants';
import { getPrevNext } from '~/utils/getPrevNext';
import { useKeyPress } from '~/utils/hooks/hooks';
import { isMarkdownLinkTitle } from '~/utils/isMarkdownLink';
import { prefetch } from '~/utils/prefetch';
import { isModalEvent, legacyClick } from '~/utils/utils';
import * as styles from './PrevNext.css';

export const EmptyPrevNext = ({ style }: { style?: React.CSSProperties }) => {
  return (
    <div className={styles.container} style={style}>
      <div className={pageStyles.bottomNavigationButton}>
        <div className={pageStyles.paginationSubLabel}>« Previous</div>
        <div className={pageStyles.paginationLabel}>
          <Skeleton width="40%" />
        </div>
      </div>
      <div className={pageStyles.bottomNavigationButton} style={{ textAlign: 'right' }}>
        <div className={pageStyles.paginationSubLabel}>Next »</div>
        <div className={cx(pageStyles.paginationLabel, flex, justifyEnd)}>
          <Skeleton width="40%" />
        </div>
      </div>
    </div>
  );
};

const PrevNext = ({ file, files, wiki }: { file: DriveFile; files: DriveFile[]; wiki: DriveFile }) => {
  const { cache } = useSWRConfig();
  const { prev, next } = getPrevNext({
    file,
    files,
    wiki,
  });

  // Prefetch prev & next
  // const prevLinkRef = useRef<HTMLAnchorElement>(null);
  // const nextLinkRef = useRef<HTMLAnchorElement>(null);
  // useIsVisible(prevLinkRef, disablePrevNextRequest ? () => {} : () => prev && prefetch(prev, cache));
  // useIsVisible(nextLinkRef, disablePrevNextRequest ? () => {} : () => next && prefetch(next, cache));

  const legacyPrevTo = prev ? (LEGACY ? `/app/page/${prev.id}?p=${wiki.id}` : `/app/page/${prev.id}`) : '';
  const legacyNextTo = next ? (LEGACY ? `/app/page/${next.id}?p=${wiki.id}` : `/app/page/${next.id}`) : '';

  // Keyboard support for left and right
  const navigate = useLocation()[1];
  useKeyPress('ArrowLeft', (e) => {
    if (isModalEvent(e)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || window.getSelection()?.isCollapsed === false || !wiki.id)
      return;
    e.preventDefault();
    if (prev) {
      LEGACY ? window.legacyHistory?.push(legacyPrevTo) : navigate(`/app/page/${wiki.id}/${prev.id}${search}`);
    }
  });

  useKeyPress('ArrowRight', (e) => {
    if (isModalEvent(e)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || !wiki.id || window.getSelection()?.isCollapsed === false)
      return;
    if (next) {
      LEGACY ? window.legacyHistory?.push(legacyNextTo) : navigate(`/app/page/${wiki.id}/${next.id}${search}`);
    }
  });

  const prevExternalLink = prev?.name ? isMarkdownLinkTitle(prev.name) : undefined;
  const nextExternalLink = next?.name ? isMarkdownLinkTitle(next.name) : undefined;
  const prevName = prevExternalLink ? prevExternalLink.name : prev?.name;
  const nextName = nextExternalLink ? nextExternalLink.name : next?.name;

  if (!wiki || (!prev && !next)) return null;

  return (
    <div className={styles.container}>
      <Link
        className={pageStyles.bottomNavigationButton}
        onMouseDown={() => prev && prefetch(prev, cache)}
        // hover prefetch
        // onMouseEnter={() => prev && prefetchTimeout(prev, cache, 500)}
        // onMouseLeave={() => prev && clearPrefetchTimeout(prev)}
        to={LEGACY ? legacyPrevTo : `/app/page/${wiki.id}/${prev?.id}${search}`}
        onClick={legacyClick}
        style={{
          visibility: !prev ? 'hidden' : undefined,
        }}
      >
        <div className={pageStyles.paginationSubLabel}>« Previous</div>
        <div className={pageStyles.paginationLabel}>{prevName}</div>
      </Link>
      <Link
        className={pageStyles.bottomNavigationButton}
        onMouseDown={() => next && prefetch(next, cache)}
        // hover prefetch
        // onMouseEnter={() => next && prefetchTimeout(next, cache, 500)}
        // onMouseLeave={() => next && clearPrefetchTimeout(next)}
        to={LEGACY ? legacyNextTo : `/app/page/${wiki.id}/${next?.id}${search}`}
        onClick={legacyClick}
        style={{
          textAlign: 'right',
          visibility: !next ? 'hidden' : undefined,
        }}
      >
        <div className={pageStyles.paginationSubLabel}>Next »</div>
        <div className={pageStyles.paginationLabel}>{nextName}</div>
      </Link>
    </div>
  );
};
export default PrevNext;
